import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

/**
 * Global gate that appears whenever a logged-in user has no user_type yet.
 * Ensures Google-OAuth signups and any legacy accounts pick buyer/seller/both.
 */
export default function UserTypeGate() {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) { setOpen(false); return; }
    setOpen(!user.user_type);
  }, [user]);

  if (!user || !open) return null;

  const save = async (t) => {
    setBusy(true);
    try {
      await api.post("/me/user-type", { user_type: t });
      await refresh();
      toast.success("Welcome to Tharlo!");
      setOpen(false);
      // Optional: send to right dashboard on first pick
      const url = new URL(window.location.href);
      if (url.searchParams.get("need_user_type")) {
        url.searchParams.delete("need_user_type");
        window.location.href = t === "buyer" ? "/dashboard" : "/broker";
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[120] bg-ink/80 backdrop-blur-sm flex items-start justify-center p-4 pt-10 overflow-y-auto" data-testid="user-type-gate">
      <div className="bg-white w-full max-w-md border border-border mb-8">
        <div className="p-5 border-b border-border">
          <div className="overline">Welcome, {user.name?.split(" ")[0] || "there"}</div>
          <h2 className="font-display text-xl text-ink mt-0.5">How will you use Tharlo?</h2>
          <p className="text-xs text-muted-foreground mt-1">You can change this later from your profile.</p>
        </div>
        <div className="p-5 space-y-3">
          <button onClick={() => save("buyer")} disabled={busy} data-testid="gate-user-type-buyer" className="w-full flex items-start gap-3 border border-border hover:border-ink p-4 text-left">
            <div className="w-8 h-8 bg-gold/10 text-gold flex items-center justify-center shrink-0 font-bold">B</div>
            <div>
              <div className="font-semibold text-ink text-sm">I'm a Buyer</div>
              <div className="text-xs text-muted-foreground">Browse properties, save favourites, post requirements</div>
            </div>
          </button>
          <button onClick={() => save("seller")} disabled={busy} data-testid="gate-user-type-seller" className="w-full flex items-start gap-3 border border-border hover:border-ink p-4 text-left">
            <div className="w-8 h-8 bg-gold/10 text-gold flex items-center justify-center shrink-0 font-bold">S</div>
            <div>
              <div className="font-semibold text-ink text-sm">I'm a Seller / Broker / Developer</div>
              <div className="text-xs text-muted-foreground">List properties, manage leads, get verified</div>
            </div>
          </button>
          <button onClick={() => save("both")} disabled={busy} data-testid="gate-user-type-both" className="w-full flex items-start gap-3 border border-border hover:border-ink p-4 text-left">
            <div className="w-8 h-8 bg-ink text-gold flex items-center justify-center shrink-0 font-bold">B+S</div>
            <div>
              <div className="font-semibold text-ink text-sm">Both — I buy and sell</div>
              <div className="text-xs text-muted-foreground">Full access to buyer & seller dashboards</div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}
